import React, { useMemo, useState } from 'react';
import { AppButton } from '../AppButton/AppButton';
import { X, Eye } from 'react-feather';
import { COLORS } from '../../../utils/colors';
import emptyListImg from '../../../assets/images/emptyList.png';
import { RequestedSupplierJobApiResponse, SupplierJobAddressDetails } from '@dill/dill-shared';
import { twMerge } from 'tailwind-merge';
import moment from 'moment';
import { AppDropDown2 } from '../AppDropDown2/AppDropDown2';
import { useAppSelector } from '../../../redux/store';
import { DownloadSimple } from 'phosphor-react';

const RequestedJobView = ({
  onClose,
  requestedSupplierJob,
  onAcceptSupplierJob,
  onEditSupplierJob,
  onDeclineSupplierJob,
  accepting,
  declining,
  onPreviewAttachment,
  onDownloadAttachment,
}: {
  onClose: () => void;
  requestedSupplierJob: RequestedSupplierJobApiResponse;
  onAcceptSupplierJob: (data: RequestedSupplierJobApiResponse) => void;
  onEditSupplierJob: (data: RequestedSupplierJobApiResponse) => void;
  onDeclineSupplierJob: (data: RequestedSupplierJobApiResponse) => void;
  accepting: boolean;
  declining: boolean;
  onPreviewAttachment: (url: string, fileType: string) => void;
  onDownloadAttachment: (url: string, name: string) => void;
}) => {
  const { supplierBuyers } = useAppSelector((state) => state.supplierBuyers);
  const [selectedBuyerId, setSelectedBuyerId] = useState<string>(
    requestedSupplierJob.buyerId || ''
  );
  const [buyerError, setBuyerError] = useState<string>('');
  const fetchedSupplierBuyers = useMemo(() => {
    if (supplierBuyers.length > 0) {
      return supplierBuyers;
    }
    return [];
  }, [supplierBuyers]);
  const buyerSelected = useMemo(() => {
    return supplierBuyers.find((buyer) => buyer.id === requestedSupplierJob.buyerId);
  }, [requestedSupplierJob, supplierBuyers]);

  return (
    <div className="flex w-[560px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg  flex-col">
      <div>
        <h2 className="text-lg font-semibold mx-4 my-3">{'Review Job'}</h2>
        <X
          className="absolute right-4 top-4 cursor-pointer"
          color={COLORS.GREY_500}
          onClick={onClose}
        />
      </div>
      <div style={{ height: '0.01em' }} className="h-0.5 w-full bg-slate-400"></div>
      <div className="max-h-[calc(90vh-120px)] p-4 mt-1 overflow-y-auto">
        <div className="text-xs font-semibold">Job Details</div>
        {requestedSupplierJob.customerAddress &&
          requestedSupplierJob.customerAddress.address !== '' &&
          buyerSelected && (
            <div className="w-full p-2 rounded border GREY_200-BORDER PRIMARY_50-BG mt-2">
              <div>
                <div className="text-xs GREY_500-CLR">Customer Address</div>
                {requestedSupplierJob.customerAddress.address !== '' &&
                requestedSupplierJob.customerAddress.address !== buyerSelected.address ? (
                  <div className="flex items-center">
                    <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                      {buyerSelected.address ?? ''}
                    </div>
                    <div className="text-xs font-semibold">
                      {requestedSupplierJob.customerAddress.address}
                    </div>
                  </div>
                ) : (
                  <div className="text-xs capitalize font-semibold">
                    {requestedSupplierJob.customerAddress.address}
                  </div>
                )}
              </div>
              <div className="flex justify-between w-full mt-3">
                <div className="w-1/3">
                  <div className="text-xs GREY_500-CLR">Customer City</div>
                  {requestedSupplierJob.customerAddress.city !== '' &&
                  requestedSupplierJob.customerAddress.city !== buyerSelected.city ? (
                    <div className="flex items-center">
                      <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                        {buyerSelected.city ?? ''}
                      </div>
                      <div className="text-xs font-semibold">
                        {requestedSupplierJob.customerAddress.city}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs capitalize font-semibold">
                      {requestedSupplierJob.customerAddress.city}
                    </div>
                  )}
                </div>
                <div className="w-1/3">
                  <div className="text-xs GREY_500-CLR">Customer State</div>
                  {requestedSupplierJob.customerAddress.state !== '' &&
                  requestedSupplierJob.customerAddress.state !== buyerSelected.state ? (
                    <div className="flex items-center">
                      <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                        {buyerSelected.state ?? ''}
                      </div>
                      <div className="text-xs font-semibold">
                        {requestedSupplierJob.customerAddress.state}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs capitalize font-semibold">
                      {requestedSupplierJob.customerAddress.state}
                    </div>
                  )}
                </div>
                <div className="w-1/3">
                  <div className="text-xs GREY_500-CLR">Customer Zip Code</div>
                  {requestedSupplierJob.customerAddress.zipCode !== '' &&
                  requestedSupplierJob.customerAddress.zipCode !== buyerSelected.zipCode ? (
                    <div className="flex items-center">
                      <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                        {buyerSelected.zipCode ?? ''}
                      </div>
                      <div className="text-xs font-semibold">
                        {requestedSupplierJob.customerAddress.zipCode}
                      </div>
                    </div>
                  ) : (
                    <div className="text-xs font-semibold">
                      {requestedSupplierJob.customerAddress.zipCode}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        <div className="w-full p-2 rounded border GREY_200-BORDER PRIMARY_50-BG mt-2">
          {requestedSupplierJob.buyerId === '' ? (
            <div>
              <div className="text-xs GREY_500-CLR">Customer</div>
              <div className="text-xs capitalize font-semibold mb-2">
                {requestedSupplierJob.buyerName.toLowerCase()}
              </div>
              <AppDropDown2
                label="Customer"
                value={selectedBuyerId}
                errorText={buyerError}
                items={[
                  ...fetchedSupplierBuyers.map((buyer) => {
                    return {
                      label: buyer.name,
                      value: buyer.id,
                    };
                  }),
                ]}
                onSelectChange={(item) => {
                  if (item && item?.value !== null) {
                    setBuyerError('');
                    setSelectedBuyerId(item.value);
                  }
                }}
                placeholder="Select corresponding customer"
              />
            </div>
          ) : (
            <div>
              <div className="text-xs GREY_500-CLR">Customer</div>
              <div className="text-xs capitalize font-semibold">
                {requestedSupplierJob.buyerName.toLowerCase()}
              </div>
            </div>
          )}
          <div className="flex justify-between w-full mt-3">
            <div className="w-1/2">
              <div className="text-xs GREY_500-CLR">Job Name</div>
              {requestedSupplierJob.supplierJob?.name !== '' &&
              requestedSupplierJob.supplierJob?.name !== requestedSupplierJob.name ? (
                <div className="flex items-center">
                  <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                    {requestedSupplierJob.supplierJob?.name ?? ''}
                  </div>
                  <div className="text-xs font-semibold">{requestedSupplierJob.name}</div>
                </div>
              ) : (
                <div className="text-xs font-semibold">{requestedSupplierJob.name}</div>
              )}
            </div>
            {requestedSupplierJob.jobNumber && (
              <div className="w-1/2">
                <div className="text-xs GREY_500-CLR">Job Number</div>
                {requestedSupplierJob.supplierJob?.jobNumber !== '' &&
                requestedSupplierJob.supplierJob?.jobNumber !== requestedSupplierJob.jobNumber ? (
                  <div className="flex items-center">
                    <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                      {requestedSupplierJob.supplierJob?.jobNumber ?? ''}
                    </div>
                    <div className="text-xs font-semibold">{requestedSupplierJob.jobNumber}</div>
                  </div>
                ) : (
                  <div className="text-xs font-semibold">{requestedSupplierJob.jobNumber}</div>
                )}
              </div>
            )}
          </div>
          <div className="mt-3">
            <div className="text-xs GREY_500-CLR">Job Address</div>
            {requestedSupplierJob.supplierJob?.jobAddress !== '' &&
            requestedSupplierJob.supplierJob?.jobAddress !== requestedSupplierJob.jobAddress ? (
              <div className="flex items-center">
                <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                  {requestedSupplierJob.supplierJob?.jobAddress ?? ''}
                </div>
                <div className="text-xs font-semibold">{requestedSupplierJob.jobAddress}</div>
              </div>
            ) : (
              <div className="text-xs capitalize font-semibold">
                {requestedSupplierJob.jobAddress}
              </div>
            )}
          </div>
          <div className="flex justify-between w-full mt-3">
            <div className="w-1/3">
              <div className="text-xs GREY_500-CLR">Job City</div>
              {requestedSupplierJob.supplierJob?.jobCity !== '' &&
              requestedSupplierJob.supplierJob?.jobCity !== requestedSupplierJob.jobCity ? (
                <div className="flex items-center">
                  <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                    {requestedSupplierJob.supplierJob?.jobCity ?? ''}
                  </div>
                  <div className="text-xs font-semibold">{requestedSupplierJob.jobCity}</div>
                </div>
              ) : (
                <div className="text-xs capitalize font-semibold">
                  {requestedSupplierJob.jobCity}
                </div>
              )}
            </div>
            <div className="w-1/3">
              <div className="text-xs GREY_500-CLR">Job State</div>
              {requestedSupplierJob.supplierJob?.jobState !== '' &&
              requestedSupplierJob.supplierJob?.jobState !== requestedSupplierJob.jobState ? (
                <div className="flex items-center">
                  <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                    {requestedSupplierJob.supplierJob?.jobState ?? ''}
                  </div>
                  <div className="text-xs font-semibold">{requestedSupplierJob.jobState}</div>
                </div>
              ) : (
                <div className="text-xs capitalize font-semibold">
                  {requestedSupplierJob.jobState}
                </div>
              )}
            </div>
            <div className="w-1/3">
              <div className="text-xs GREY_500-CLR">Job Zip Code</div>
              {requestedSupplierJob.supplierJob?.jobZipCode !== '' &&
              requestedSupplierJob.supplierJob?.jobZipCode !== requestedSupplierJob.jobZipCode ? (
                <div className="flex items-center">
                  <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                    {requestedSupplierJob.supplierJob?.jobZipCode ?? ''}
                  </div>
                  <div className="text-xs font-semibold">{requestedSupplierJob.jobZipCode}</div>
                </div>
              ) : (
                <div className="text-xs font-semibold">{requestedSupplierJob.jobZipCode}</div>
              )}
            </div>
          </div>
        </div>
        <div className="w-full p-2 rounded border GREY_200-BORDER PRIMARY_50-BG mt-2">
          {requestedSupplierJob.jobState === 'AZ' &&
            requestedSupplierJob.jobLegalDescription &&
            requestedSupplierJob.jobCounty && (
              <div className="flex justify-between w-full mt-3">
                {requestedSupplierJob.jobCounty && (
                  <div className="w-1/2">
                    <div className="text-xs GREY_500-CLR">Job County</div>
                    {requestedSupplierJob.supplierJob?.jobCounty !== '' &&
                    requestedSupplierJob.supplierJob?.jobCounty !==
                      requestedSupplierJob.jobCounty ? (
                      <div className="flex items-center">
                        <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                          {requestedSupplierJob.supplierJob?.jobCounty ?? ''}
                        </div>
                        <div className="text-xs font-semibold">
                          {requestedSupplierJob.jobCounty}
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs font-semibold">{requestedSupplierJob.jobCounty}</div>
                    )}
                  </div>
                )}
                {requestedSupplierJob.jobLegalDescription && (
                  <div className="w-1/2">
                    <div className="text-xs GREY_500-CLR">Job Legal Description</div>
                    {requestedSupplierJob.supplierJob?.jobLegalDescription !== '' &&
                    requestedSupplierJob.supplierJob?.jobLegalDescription !==
                      requestedSupplierJob.jobLegalDescription ? (
                      <div className="flex items-center">
                        <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                          {requestedSupplierJob.supplierJob?.jobLegalDescription ?? ''}
                        </div>
                        <div className="text-xs font-semibold">
                          {requestedSupplierJob.jobLegalDescription}
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs font-semibold">
                        {requestedSupplierJob.jobLegalDescription}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          <div className="flex justify-between w-full mt-3">
            <div className="w-1/3">
              <div className="text-xs GREY_500-CLR">Job Type</div>
              {requestedSupplierJob.supplierJob?.jobType &&
              requestedSupplierJob.supplierJob?.jobType !== requestedSupplierJob.jobType ? (
                <div className="flex items-center">
                  <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                    {requestedSupplierJob.supplierJob?.jobType ?? ''}
                  </div>
                  <div className="text-xs font-semibold">{requestedSupplierJob.jobType}</div>
                </div>
              ) : (
                <div className="text-xs capitalize font-semibold">
                  {requestedSupplierJob.jobType}
                </div>
              )}
            </div>
            <div className="w-1/3">
              <div className="text-xs GREY_500-CLR">Job Sector</div>
              {requestedSupplierJob.supplierJob?.jobSector &&
              requestedSupplierJob.supplierJob?.jobSector !== requestedSupplierJob.jobSector ? (
                <div className="flex items-center">
                  <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                    {requestedSupplierJob.supplierJob?.jobSector ?? ''}
                  </div>
                  <div className="text-xs font-semibold">{requestedSupplierJob.jobSector}</div>
                </div>
              ) : (
                <div className="text-xs capitalize font-semibold">
                  {requestedSupplierJob.jobSector}
                </div>
              )}
            </div>
            <div className="w-1/3">
              <div className="text-xs GREY_500-CLR">APN</div>
              {requestedSupplierJob.supplierJob?.apn !== '' &&
              requestedSupplierJob.supplierJob?.apn !== requestedSupplierJob.apn ? (
                <div className="flex items-center">
                  <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                    {requestedSupplierJob.supplierJob?.apn ?? ''}
                  </div>
                  <div className="text-xs font-semibold">{requestedSupplierJob.apn}</div>
                </div>
              ) : (
                <div className="text-xs capitalize font-semibold">
                  {requestedSupplierJob.apn ?? 'N/A'}
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-between w-full mt-3">
            <div className="w-1/2">
              <div className="text-xs GREY_500-CLR">Estimated Material Amount</div>
              {requestedSupplierJob.supplierJob?.estimatedMaterialAmount !== '' &&
              requestedSupplierJob.supplierJob?.estimatedMaterialAmount !==
                requestedSupplierJob.estimatedMaterialAmount ? (
                <div className="flex items-center">
                  <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                    {requestedSupplierJob.supplierJob?.estimatedMaterialAmount ?? ''}
                  </div>
                  <div className="text-xs font-semibold">
                    {requestedSupplierJob.estimatedMaterialAmount}
                  </div>
                </div>
              ) : (
                <div className="text-xs capitalize font-semibold">
                  {requestedSupplierJob.estimatedMaterialAmount}
                </div>
              )}
            </div>
            <div className="w-1/2">
              <div className="text-xs GREY_500-CLR">Job Furnishings</div>
              {requestedSupplierJob.supplierJob?.jobFurnishings !== '' &&
              requestedSupplierJob.supplierJob?.jobFurnishings !==
                requestedSupplierJob.jobFurnishings ? (
                <div className="flex items-center">
                  <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                    {requestedSupplierJob.supplierJob?.jobFurnishings ?? ''}
                  </div>
                  <div className="text-xs font-semibold">{requestedSupplierJob.jobFurnishings}</div>
                </div>
              ) : (
                <div className="text-xs capitalize font-semibold">
                  {requestedSupplierJob.jobFurnishings}
                </div>
              )}
            </div>
          </div>
        </div>
        {requestedSupplierJob.owners.length > 0 && (
          <div className="w-full p-2 rounded border GREY_200-BORDER PRIMARY_50-BG mt-2">
            <div className="text-xs font-bold">Owner</div>
            {requestedSupplierJob.owners.map((owner, index) => {
              const requestedOwner =
                requestedSupplierJob.supplierJob?.owners[index] ??
                ({} as SupplierJobAddressDetails);
              return (
                <div key={`owner-review-${index}`} className="border-t GREY_200-BORDER pt-2 mt-2">
                  <div>
                    <div className="text-xs GREY_500-CLR">Owner</div>
                    {requestedOwner?.name !== '' && requestedOwner?.name !== owner.name ? (
                      <div className="flex items-center">
                        <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                          {requestedOwner?.name ?? ''}
                        </div>
                        <div className="text-xs font-semibold">{owner.name}</div>
                      </div>
                    ) : (
                      <div className="text-xs capitalize font-semibold">{owner.name}</div>
                    )}
                  </div>
                  <div className="mt-3">
                    <div className="text-xs GREY_500-CLR">Owner Address</div>
                    {requestedOwner.address !== '' && requestedOwner.address !== owner.address ? (
                      <div className="flex items-center">
                        <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                          {requestedOwner.address ?? ''}
                        </div>
                        <div className="text-xs font-semibold">{owner.address}</div>
                      </div>
                    ) : (
                      <div className="text-xs capitalize font-semibold">{owner.address}</div>
                    )}
                  </div>
                  <div className="flex justify-between w-full mt-3">
                    <div className="w-1/3">
                      <div className="text-xs GREY_500-CLR">Owner City</div>
                      {requestedOwner.city !== '' && requestedOwner.city !== owner.city ? (
                        <div className="flex items-center">
                          <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                            {requestedOwner.city ?? ''}
                          </div>
                          <div className="text-xs font-semibold">{owner.city}</div>
                        </div>
                      ) : (
                        <div className="text-xs capitalize font-semibold">{owner.city}</div>
                      )}
                    </div>
                    <div className="w-1/3">
                      <div className="text-xs GREY_500-CLR">Owner State</div>
                      {requestedOwner.state !== '' && requestedOwner.state !== owner.state ? (
                        <div className="flex items-center">
                          <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                            {requestedOwner.state ?? ''}
                          </div>
                          <div className="text-xs font-semibold">{owner.state}</div>
                        </div>
                      ) : (
                        <div className="text-xs capitalize font-semibold">{owner.state}</div>
                      )}
                    </div>
                    <div className="w-1/3">
                      <div className="text-xs GREY_500-CLR">Owner Zip Code</div>
                      {requestedOwner.zipCode !== '' && requestedOwner.zipCode !== owner.zipCode ? (
                        <div className="flex items-center">
                          <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                            {requestedOwner.zipCode ?? ''}
                          </div>
                          <div className="text-xs font-semibold">{owner.zipCode}</div>
                        </div>
                      ) : (
                        <div className="text-xs font-semibold">{owner.zipCode}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {requestedSupplierJob.GCs.length > 0 && (
          <div className="w-full p-2 rounded border GREY_200-BORDER PRIMARY_50-BG mt-2">
            <div className="text-xs font-bold">General Contractor</div>
            {requestedSupplierJob.GCs.map((gc, index) => {
              const requestedGC =
                requestedSupplierJob.supplierJob?.GCs[index] ?? ({} as SupplierJobAddressDetails);
              return (
                <div key={`gc-review-${index}`} className="border-t GREY_200-BORDER pt-2 mt-2">
                  <div className="flex justify-between w-full">
                    <div className="w-1/2">
                      <div className="text-xs GREY_500-CLR">General Contractor</div>
                      {requestedGC.name !== '' && requestedGC.name !== gc.name ? (
                        <div className="flex items-center">
                          <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                            {requestedGC.name ?? ''}
                          </div>
                          <div className="text-xs font-semibold">{gc.name}</div>
                        </div>
                      ) : (
                        <div className="text-xs capitalize font-semibold">{gc.name}</div>
                      )}
                    </div>
                    <div className="w-1/2">
                      <div className="text-xs GREY_500-CLR">Customer Type</div>
                      {requestedSupplierJob.supplierJob?.customerType &&
                      requestedSupplierJob.supplierJob?.customerType !==
                        requestedSupplierJob.customerType ? (
                        <div className="flex items-center">
                          <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                            {requestedSupplierJob.supplierJob?.customerType ?? ''}
                          </div>
                          <div className="text-xs font-semibold">
                            {requestedSupplierJob.customerType}
                          </div>
                        </div>
                      ) : (
                        <div className="text-xs capitalize font-semibold">
                          {requestedSupplierJob.customerType}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-3">
                    <div className="text-xs GREY_500-CLR">GC Address</div>
                    {requestedGC.address !== '' && requestedGC.address !== gc.address ? (
                      <div className="flex items-center">
                        <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                          {requestedGC.address ?? ''}
                        </div>
                        <div className="text-xs font-semibold">{gc.address}</div>
                      </div>
                    ) : (
                      <div className="text-xs capitalize font-semibold">{gc.address}</div>
                    )}
                  </div>
                  <div className="flex justify-between w-full mt-3">
                    <div className="w-1/3">
                      <div className="text-xs GREY_500-CLR">GC City</div>
                      {requestedGC.city !== '' && requestedGC.city !== gc.city ? (
                        <div className="flex items-center">
                          <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                            {requestedGC.city ?? ''}
                          </div>
                          <div className="text-xs font-semibold">{gc.city}</div>
                        </div>
                      ) : (
                        <div className="text-xs capitalize font-semibold">{gc.city}</div>
                      )}
                    </div>
                    <div className="w-1/3">
                      <div className="text-xs GREY_500-CLR">GC State</div>
                      {requestedGC.state !== '' && requestedGC.state !== gc.state ? (
                        <div className="flex items-center">
                          <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                            {requestedGC.state ?? ''}
                          </div>
                          <div className="text-xs font-semibold">{gc.state}</div>
                        </div>
                      ) : (
                        <div className="text-xs capitalize font-semibold">{gc.state}</div>
                      )}
                    </div>
                    <div className="w-1/3">
                      <div className="text-xs GREY_500-CLR">GC Zip Code</div>
                      {requestedGC.zipCode !== '' && requestedGC.zipCode !== gc.zipCode ? (
                        <div className="flex items-center">
                          <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                            {requestedGC.zipCode ?? ''}
                          </div>
                          <div className="text-xs font-semibold">{gc.zipCode}</div>
                        </div>
                      ) : (
                        <div className="text-xs font-semibold">{gc.zipCode}</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        {!requestedSupplierJob.hasNoLender ? (
          <div className="w-full p-2 rounded border GREY_200-BORDER PRIMARY_50-BG mt-2">
            <div className="text-xs font-bold">Lender</div>
            {requestedSupplierJob.lenders &&
              requestedSupplierJob.lenders
                .filter((lender) => lender.name !== '')
                .map((lender, index) => {
                  const requestedLender =
                    requestedSupplierJob?.supplierJob?.lenders?.[index] ??
                    ({} as SupplierJobAddressDetails);
                  return (
                    <div
                      key={`lenders-review-${index}`}
                      className="border-t GREY_200-BORDER pt-2 mt-2">
                      <div>
                        <div className="text-xs GREY_500-CLR">Lender</div>
                        {requestedLender.name !== '' && requestedLender.name !== lender.name ? (
                          <div className="flex items-center">
                            <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                              {requestedLender.name ?? ''}
                            </div>
                            <div className="text-xs font-semibold">{lender.name}</div>
                          </div>
                        ) : (
                          <div className="text-xs capitalize font-semibold">{lender.name}</div>
                        )}
                      </div>
                      <div className="mt-3">
                        <div className="text-xs GREY_500-CLR">Lender Address</div>
                        {requestedLender.address !== '' &&
                        requestedLender.address !== lender.address ? (
                          <div className="flex items-center">
                            <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                              {requestedLender.address ?? ''}
                            </div>
                            <div className="text-xs font-semibold">{lender.address}</div>
                          </div>
                        ) : (
                          <div className="text-xs capitalize font-semibold">{lender.address}</div>
                        )}
                      </div>
                      <div className="flex justify-between w-full mt-3">
                        <div className="w-1/3">
                          <div className="text-xs GREY_500-CLR">Lender City</div>
                          {requestedLender.city !== '' && requestedLender.city !== lender.city ? (
                            <div className="flex items-center">
                              <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                                {requestedLender.city ?? ''}
                              </div>
                              <div className="text-xs font-semibold">{lender.city}</div>
                            </div>
                          ) : (
                            <div className="text-xs capitalize font-semibold">{lender.city}</div>
                          )}
                        </div>
                        <div className="w-1/3">
                          <div className="text-xs GREY_500-CLR">Lender State</div>
                          {requestedLender.state !== '' &&
                          requestedLender.state !== lender.state ? (
                            <div className="flex items-center">
                              <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                                {requestedLender.state ?? ''}
                              </div>
                              <div className="text-xs font-semibold">{lender.state}</div>
                            </div>
                          ) : (
                            <div className="text-xs capitalize font-semibold">{lender.state}</div>
                          )}
                        </div>
                        <div className="w-1/3">
                          <div className="text-xs GREY_500-CLR">Lender Zip Code</div>
                          {requestedLender.zipCode !== '' &&
                          requestedLender.zipCode !== lender.zipCode ? (
                            <div className="flex items-center">
                              <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                                {requestedLender.zipCode ?? ''}
                              </div>
                              <div className="text-xs font-semibold">{lender.zipCode}</div>
                            </div>
                          ) : (
                            <div className="text-xs font-semibold">{lender.zipCode}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
          </div>
        ) : (
          <div className="w-full p-2 rounded border GREY_200-BORDER PRIMARY_50-BG mt-2">
            <div>
              <div className="text-xs GREY_500-CLR">Lender</div>
              <div className="text-xs capitalize font-semibold">N/A</div>
            </div>
          </div>
        )}
        {requestedSupplierJob.isJobBonded ? (
          <div className="w-full p-2 rounded border GREY_200-BORDER PRIMARY_50-BG mt-2">
            <div className="text-xs font-bold">Surety</div>
            {requestedSupplierJob.sureties &&
              requestedSupplierJob.sureties
                .filter((surety) => surety.name !== '')
                .map((surety, index) => {
                  const requestedSurety =
                    requestedSupplierJob?.supplierJob?.sureties?.[index] ??
                    ({} as SupplierJobAddressDetails);
                  return (
                    <div
                      key={`surety-review-${index}`}
                      className="border-t GREY_200-BORDER pt-2 mt-2">
                      <div>
                        <div className="text-xs GREY_500-CLR">Surety</div>
                        {requestedSurety.name !== '' && requestedSurety.name !== surety.name ? (
                          <div className="flex items-center">
                            <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                              {requestedSurety.name ?? ''}
                            </div>
                            <div className="text-xs font-semibold">{surety.name}</div>
                          </div>
                        ) : (
                          <div className="text-xs capitalize font-semibold">{surety.name}</div>
                        )}
                      </div>
                      <div className="mt-3">
                        <div className="text-xs GREY_500-CLR">Surety Address</div>
                        {requestedSurety.address !== '' &&
                        requestedSurety.address !== surety.address ? (
                          <div className="flex items-center">
                            <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                              {requestedSurety.address ?? ''}
                            </div>
                            <div className="text-xs font-semibold">{surety.address}</div>
                          </div>
                        ) : (
                          <div className="text-xs capitalize font-semibold">{surety.address}</div>
                        )}
                      </div>
                      <div className="flex justify-between w-full mt-3">
                        <div className="w-1/3">
                          <div className="text-xs GREY_500-CLR">Surety City</div>
                          {requestedSurety.city !== '' && requestedSurety.city !== surety.city ? (
                            <div className="flex items-center">
                              <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                                {requestedSurety.city ?? ''}
                              </div>
                              <div className="text-xs font-semibold">{surety.city}</div>
                            </div>
                          ) : (
                            <div className="text-xs capitalize font-semibold">{surety.city}</div>
                          )}
                        </div>
                        <div className="w-1/3">
                          <div className="text-xs GREY_500-CLR">Surety State</div>
                          {requestedSurety.state !== '' &&
                          requestedSurety.state !== surety.state ? (
                            <div className="flex items-center">
                              <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                                {requestedSurety.state ?? ''}
                              </div>
                              <div className="text-xs font-semibold">{surety.state}</div>
                            </div>
                          ) : (
                            <div className="text-xs capitalize font-semibold">{surety.state}</div>
                          )}
                        </div>
                        <div className="w-1/3">
                          <div className="text-xs GREY_500-CLR">Surety Zip</div>
                          {requestedSurety.zipCode !== '' &&
                          requestedSurety.zipCode !== surety.zipCode ? (
                            <div className="flex items-center">
                              <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                                {requestedSurety.zipCode ?? ''}
                              </div>
                              <div className="text-xs font-semibold">{surety.zipCode}</div>
                            </div>
                          ) : (
                            <div className="text-xs capitalize font-semibold">{surety.zipCode}</div>
                          )}
                        </div>
                      </div>
                      <div className="mt-3">
                        <div className="text-xs GREY_500-CLR">Bond Number</div>
                        {requestedSurety.bondNumber !== '' &&
                        requestedSurety.bondNumber !== surety.bondNumber ? (
                          <div className="flex items-center">
                            <div className="text-xs font-semibold ERROR_500-CLR mr-1 line-through">
                              {requestedSurety.bondNumber ?? ''}
                            </div>
                            <div className="text-xs font-semibold">{surety.bondNumber ?? ''}</div>
                          </div>
                        ) : (
                          <div className="text-xs capitalize font-semibold">
                            {surety.bondNumber ?? ''}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
          </div>
        ) : (
          <div className="w-full p-2 rounded border GREY_200-BORDER PRIMARY_50-BG mt-2">
            <div>
              <div className="text-xs GREY_500-CLR">Is Job Bonded?</div>
              <div className="text-xs capitalize font-semibold">No</div>
            </div>
          </div>
        )}
        {requestedSupplierJob?.jobInfoSheetAttachments &&
          requestedSupplierJob?.jobInfoSheetAttachments.length > 0 && (
            <div className="w-full p-2 rounded border GREY_200-BORDER PRIMARY_50-BG mt-2">
              <div className="text-xs GREY_500-CLR mb-1">Job Info Sheet Attachments</div>
              {requestedSupplierJob?.jobInfoSheetAttachments.map((attachment, index) => (
                <div
                  className="flex gap-2 items-center mb-1"
                  key={`requested-info-attachment-${index}`}>
                  {(!attachment?.fileType ||
                    (attachment?.fileType &&
                      (attachment?.fileType === 'pdf' ||
                        attachment?.fileType === 'png' ||
                        attachment?.fileType === 'jpg' ||
                        attachment?.fileType === 'jpeg'))) && (
                    <AppButton
                      type="TERTIARY"
                      buttonStyles={{
                        padding: '4px',
                        height: '35px',
                        width: '35px',
                      }}
                      icon={<Eye color={COLORS.GREY_500} size={18} />}
                      onClick={() => {
                        onPreviewAttachment(attachment?.url ?? '', attachment?.fileType ?? '');
                      }}
                    />
                  )}
                  <AppButton
                    type="TERTIARY"
                    buttonStyles={{
                      padding: '4px',
                      height: '35px',
                      width: '35px',
                    }}
                    icon={<DownloadSimple color={COLORS.GREY_500} size={18} />}
                    onClick={() => {
                      onDownloadAttachment(attachment?.url ?? '', attachment?.name ?? '');
                    }}
                  />
                  <div className="text-xs font-semibold">{attachment.name}</div>
                </div>
              ))}
            </div>
          )}
      </div>
      <div style={{ height: '0.01em' }} className="h-0.5 w-full bg-slate-400 my-4"></div>
      {buyerError !== '' && (
        <p className="mx-3 ERROR_500-CLR mb-1 text-xs text-center">{`See errors above.`}</p>
      )}
      <div className="flex justify-between mx-3 mb-2 gap-2">
        <div className="flex gap-2">
          <AppButton text="Close" type="TERTIARY" onClick={onClose} />
          <AppButton
            text="Decline"
            type="SECONDARY"
            onClick={() =>
              onDeclineSupplierJob({ ...requestedSupplierJob, buyerId: selectedBuyerId })
            }
            isDisabled={declining}
            isLoading={declining}
            buttonStyles={{ width: '100px', height: '40px' }}
          />
        </div>
        <div className="flex gap-2">
          {requestedSupplierJob.buyerId !== '' && (
            <AppButton
              text="Edit"
              type="SECONDARY"
              onClick={() =>
                onEditSupplierJob({ ...requestedSupplierJob, buyerId: selectedBuyerId })
              }
            />
          )}
          <AppButton
            text="Accept"
            type="PRIMARY"
            onClick={() => {
              if (selectedBuyerId === '') {
                setBuyerError('Please select a customer');
              } else {
                onAcceptSupplierJob({ ...requestedSupplierJob, buyerId: selectedBuyerId });
              }
            }}
            buttonStyles={{ width: '100px', height: '40px' }}
            isDisabled={accepting}
            isLoading={accepting}
          />
        </div>
      </div>
    </div>
  );
};

export default RequestedJobView;
